import { createFileRoute } from '@tanstack/react-router'
import { useReport } from '@/hooks/use-report'
import { useDeferredValue } from 'react'
import { useSuspenseQuery } from '@tanstack/react-query'
import { BoardPending } from './-components/shared/board-pending'
import { Board } from './-components/shared/board'
import { EmptyState } from './-components/shared/empty-state'
import { GroupContextProvider } from './-components/shared/group-context'

export const Route = createFileRoute('/_app/jobs/$jobId/board')({
  component: RouteComponent,
  pendingComponent: BoardPending,
  // SHOWN IMMEDIATELY, and held no longer than it has to be. The router's defaults are `pendingMs: 1000` /
  // `pendingMinMs: 500`: a navigation whose loaders resolve inside a second therefore renders NOTHING new and the
  // route you came from stays on screen — which on a phone means the NAV STACK cannot push, because the level it
  // would push is the route being rendered (see useRenderedPathname) and that is still the old one. Measured on a
  // device as "tapping an integrant only marks it selected; the report never opens" — the search params commit at
  // once (hence the selection) while the screen sits on the list, and tapping again "works" only because the
  // queries are cached by then. At 0/0 the skeleton is there for the first frame of the push and gone the moment
  // the data is, so a cached navigation costs at most one frame of it.
  pendingMs: 0,
  pendingMinMs: 0,
})

function RouteComponent() {
  const context = Route.useRouteContext()
  const { report } = useReport(context.reportQueryOptions)
  // Deferred so changing the type filter keeps the PREVIOUS view on screen while the new data loads —
  // the options object comes from the route's context, so this is the very object the loader warmed.
  const deferredIntegrants = useDeferredValue(context.jobIntegrantIndicatorsQueryOptions)
  const { data: jobIntegrants } = useSuspenseQuery(deferredIntegrants)
  const { data: job } = useSuspenseQuery(context.jobQueryOptions)

  const members = (jobIntegrants?.members ?? []).map((member) => ({
    ...member,
    id: member.id,
  }))

  if (jobIntegrants?.emptyStateCode) {
    return <EmptyState code={jobIntegrants.emptyStateCode} surface="board" />
  }

  return (
    <GroupContextProvider type="job">
      <Board report={report} name={job.name ?? ''} profiles={members} standardIndicators={jobIntegrants?.indicators} />
    </GroupContextProvider>
  )
}
